<script setup lang="ts">
import type { PropType } from "vue"
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { ToastVariant } from "@/types"
import { computed, useAttrs } from "vue"
import { Icon } from "@/components/ui"
import * as Icons from "@/components/ui/Icon/icons"
import { useTranslation } from "@/composables"

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  toast: {
    type: Object,
    default: () => ({}),
  },
  variant: {
    type: String as PropType<ToastVariant>,
    default: "default",
  },
})

const emit = defineEmits(["hide"])

const attrs = useAttrs()

const title = computed(() => "title" in attrs && typeof attrs.title === "string" ? attrs.title : "")
const textContent = computed(() => "textContent" in attrs && typeof attrs.textContent === "string" ? attrs.textContent : "")
const icon = computed(() => "icon" in attrs && typeof attrs.icon === "string" && attrs.icon in Icons ? attrs.icon as IconKeys : null)

const { t } = useTranslation()

const onDismiss = () => emit("hide", props.toast)
</script>

<template>
  <div
    class="gpx-toast rounded-lg" :class="[{
      'bg-primary text-primary-card-text': variant === 'default',
      'bg-theme-success text-white': variant === 'success',
      'bg-theme-danger text-white': variant === 'danger',
    }]"
  >
    <slot>
      <div v-if="variant === 'danger'" class="gpx-toast-content">
        <div v-if="title || icon" class="gpx-toast-content-title">
          <Icon v-if="icon" :icon="icon" />
          <h5>{{ title }}</h5>
        </div>
        <div v-else class="flex flex-row items-center gap-2 mb-2 whitespace-nowrap">
          <Icon icon="FolderCross" />
          <h5 class="text-base mr-4 md:text-lg">
            {{ t("something_went_wrong") }}
          </h5>
        </div>
        <p v-if="textContent">
          {{ textContent }}
        </p>
        <p v-else class="font-montserrat text-sm max-w-62.5">
          <i18n-t keypath="contact_support" scope="global">
            <template #email>
              <a href="mailto:customercare@guestpix.com" target="_blank" class="underline cursor-pointer text-white">
                {{ t("customer_care") }}
              </a>
            </template>
          </i18n-t>
        </p>
      </div>
      <div v-else class="gpx-toast-content">
        <div v-if="title || icon" class="gpx-toast-content-title">
          <Icon v-if="icon" :icon="icon" />
          <h5>{{ title }}</h5>
        </div>
        <p>{{ textContent }}</p>
      </div>
    </slot>
    <button class="gpx-toast-close-button" @click="onDismiss">
      <span class="sr-only">Dismiss Toast</span>
      <Icon icon="Close" />
    </button>
  </div>
</template>

<style scoped>
.gpx-toast {
  --icon-dimension: 1.75rem;

  display: flex;
  align-items: start;
  gap: 1rem;
  pointer-events: all;
  padding: 1rem 1.25rem;
  width: 100%;
  box-sizing: border-box;
}

.gpx-toast-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.gpx-toast-content-title {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  white-space: nowrap;
}

.gpx-toast-content-title svg {
  height: var(--icon-dimension);
  width: var(--icon-dimension);
}

.gpx-toast-content-title h5 {
  font-size: 1rem;
  margin: 0 1rem 0 0;
}

.gpx-toast-content p {
  font-family: montserrat, sans-serif;
  font-weight: var(--gpx-font-normal);
  font-size: 0.875rem;
  margin: 0;
  max-width: 250px;
}

.gpx-toast-close-button {
  cursor: pointer;
  background-color: transparent;
  border: none;
  padding: 0;
  margin-left: auto;
}

@media (width >=768px) {
  .gpx-toast-content-title h5 {
    font-size: 1.15rem;
  }
}
</style>
